import { useMemo } from "react"
import { QRCode, Table, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { Key } from "react"
import dayjs from "dayjs"
import { ID } from "@/types/common"
import { TableActionColumn } from "@/components/TableActionColumn"
import { buildPublicQrCodeUrl } from "../helpers"
import { QrCode, QrCodeTableProps } from "../types"

const { Paragraph } = Typography

const QrCodeTable = ({
  items,
  loading = false,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDelete,
  pagination,
  onPageChange,
}: QrCodeTableProps) => {
  const columns: ColumnsType<QrCode> = useMemo(
    () => [
      {
        title: "二维码名称",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
      },
      {
        title: "预览",
        dataIndex: "code",
        key: "preview",
        width: 120,
        render: (code: string) => <QRCode value={buildPublicQrCodeUrl(code)} size={72} />,
      },
      {
        title: "扫码入口",
        dataIndex: "code",
        key: "code",
        render: (code: string) => {
          const url = buildPublicQrCodeUrl(code)

          return (
            <Paragraph ellipsis={{ rows: 2, tooltip: url }} className="mb-0">
              {url}
            </Paragraph>
          )
        },
      },
      {
        title: "备用内容",
        dataIndex: "content",
        key: "content",
        render: (content?: string | null) => (
          <Paragraph ellipsis={{ rows: 2, tooltip: content || "-" }} className="mb-0">
            {content || "-"}
          </Paragraph>
        ),
      },
      {
        title: "绑定文件",
        dataIndex: "fileAsset",
        key: "fileAsset",
        width: 220,
        render: (fileAsset: QrCode["fileAsset"]) =>
          fileAsset ? (
            <div>
              <div>{fileAsset.name}</div>
              <div className="text-xs text-gray-500">{fileAsset.originalName}</div>
            </div>
          ) : (
            "-"
          ),
      },
      {
        title: "创建时间",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 180,
        render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm"),
      },
      TableActionColumn<QrCode>({
        onEdit,
        onDelete: (record: QrCode | ID) => {
          const id = typeof record === "object" ? record.id : record
          onDelete(id)
        },
        getId: (item) => item.id,
        deleteConfirmDescription: (item) => `确定要删除二维码“${item.name}”吗？`,
        width: 160,
      }) as any,
    ],
    [onDelete, onEdit],
  )

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
      }
    : undefined

  return (
    <Table
      columns={columns}
      dataSource={items}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      scroll={{ x: 1200 }}
      pagination={{
        current: pagination?.page,
        pageSize: pagination?.pageSize,
        total: pagination?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ["10", "20", "50", "100"],
        onChange: (page, pageSize) => {
          onPageChange?.(page, pageSize)
        },
      }}
    />
  )
}

export default QrCodeTable
